import React, { useEffect, useState } from "react";
import Header from "../../components/Header.jsx";
import { FaPlus, FaWallet } from "react-icons/fa";
import { GiReceiveMoney } from "react-icons/gi";
import { MdOutlineTrendingUp } from "react-icons/md";
import { IoCalendarOutline } from "react-icons/io5";
import AddIncomeModal from "./AddIncomeModal";
import Loading from "../../components/Loading/Loading";
import { useAuth } from "../../context/Auth.js";
import { toast } from "react-toastify";

const IncomeList = () => {
  const { requestWithAuth } = useAuth();
  const [incomeList, setIncomeList] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [incomeModal, setIncomeModal] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [incomeStats, setIncomeStats] = useState({
    total_income: 0,
    monthly_income: 0,
    income_count: 0
  });
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // GET INCOME LIST
  const getIncomeList = async (currentPage, search) => {
    try {
      const responseData = await requestWithAuth(
        `/finance/income/?page=${currentPage}&search=${search}&pageSize=10`,
        "GET",
        {
          "Content-Type": "application/json",
        }
      );
      setIncomeList(responseData?.results);
      setTotalPages(Math.ceil(responseData.count / 10));
    } catch (error) {
      console.error("Error fetching income list:", error);
    }
  };

  const getIncomeData = async () => {
    try {
      const responseData = await requestWithAuth(`/finance/income-data/`, 'GET', {
        "Content-Type": "application/json"
      });
      setIncomeStats(responseData);
    } catch (error) {
      toast.error("Failed to get income data");
    }
  }
  // GET INCOME TYPES
  const getIncomeTypes = async () => {
    try {
      const responseData = await requestWithAuth(
        `/finance/income-types/`,
        "GET",
        {
          "Content-Type": "application/json",
        }
      );
      setIncomeTypes(responseData);
    } catch (error) {
      console.error("Error fetching income types:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return `GHS ${parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    getIncomeList(currentPage, search);
  }, [currentPage, search]);


 
  useEffect(() => {
    const fetchData = async () => {
      await getIncomeTypes();
      await getIncomeData();
      setDataFetched(true);
    };
    fetchData();
  }, []);

  if (!dataFetched) {
    return <Loading />;
  }

  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalNumbersToShow / 2)
    );
    let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

    if (endPage - startPage + 1 < totalNumbersToShow) {
      startPage = Math.max(1, endPage - totalNumbersToShow + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, index) => (
      <button
        key={index}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`
                    min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
                    ${
                      number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                        ? "bg-white text-gray-400 border-gray-200 cursor-default"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
                `}
      >
        {number}
      </button>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {incomeModal && (
        <AddIncomeModal
          incomeTypes={incomeTypes}
          modal={incomeModal}
          setModal={setIncomeModal}
          getIncomeList={getIncomeList}
        />
      )}

      <Header title="INCOME MANAGEMENT" />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200 transform hover:scale-105 transition-transform duration-300 ease-in-out">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Income</p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {formatCurrency(incomeStats.total_income)}
              </p>
            </div>
            <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center shadow-md">
              <FaWallet className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200 transform hover:scale-105 transition-transform duration-300 ease-in-out">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                This Month's Income
              </p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {formatCurrency(incomeStats.monthly_income)}
              </p>
            </div>
            <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center shadow-md">
              <MdOutlineTrendingUp className="text-blue-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-200 transform hover:scale-105 transition-transform duration-300 ease-in-out">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Records</p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1">
                {incomeStats.income_count}
              </p>
            </div>
            <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center shadow-md">
              <GiReceiveMoney className="text-purple-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Income List Section */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center pb-4 mb-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 sm:mb-0">
            Income Records
          </h2>
          <button
            onClick={() => setIncomeModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <FaPlus className="text-base" />
            Add New Income
          </button>
        </div>

        {/* Date Filtering Options */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 flex flex-wrap items-center gap-4">
          <input className="form-select block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" type="text" placeholder="Search by income ID" onChange={(e)=>setSearch(e.target.value)} />

          {/* <label
            htmlFor="dateFilter"
            className="font-medium text-gray-700 mr-2"
          >
            Filter by Date:
          </label>
          <select
            id="dateFilter"
            className="form-select block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setCustomStartDate(""); // Reset custom dates when changing filter type
              setCustomEndDate("");
            }}
          >
            <option value="all_time">All Time</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
            <option value="custom_range">Custom Range</option>
          </select>

          {dateFilter === "custom_range" && (
            <div className="flex flex-wrap items-center gap-4 mt-2 sm:mt-0">
              <input
                type="date"
                className="form-input block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                aria-label="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                className="form-input block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                aria-label="End Date"
              />
            </div>
          )} */}
        </div>

        <div className="overflow-x-auto">
          {incomeList.length > 0 ? (
            <div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Income ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Source
                    </th>
                    
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {incomeList.map((income, index) => (
                    <tr
                      key={income.id || index}
                      className="even:bg-gray-50 hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {income.income_id || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {income.income_type?.name || income.type || "Other"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                        {formatCurrency(income.amount_received)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <IoCalendarOutline className="mr-2 text-base" />
                          {formatDate(income.date_received)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {income.payment_method || "N/A"}
                      </td>
                      
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-sm text-gray-700">
                    Showing page{" "}
                    <span className="font-medium">{currentPage}</span> of{" "}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === 1
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === totalPages
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 px-4">
              <GiReceiveMoney className="mx-auto h-16 w-16 text-gray-400 mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                No income records found for the selected period.
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Adjust your date filter or get started by adding your first
                income record.
              </p>
              <button
                onClick={() => setIncomeModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <FaPlus className="text-base" />
                Add First Income
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncomeList;
